import { Component, type OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChangelogEntryComponent } from './changelog-entry/changelog-entry.component';
import { ChangelogFilterComponent } from './changelog-filter/changelog-filter.component';
import { ChangelogSearchComponent } from './changelog-search/changelog-search.component';
import type {
  ChangelogEntry,
  ChangelogCategory,
} from '../../models/changelog/changelog.model';
import { ChangelogService } from '../../services/changelog/changelog.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-changelog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ChangelogEntryComponent,
    ChangelogFilterComponent,
    ChangelogSearchComponent,
  ],
  templateUrl: './changelog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./changelog.component.scss'],
})
export class ChangelogComponent implements OnInit {
  allEntries: ChangelogEntry[] = [];
  filteredEntries: ChangelogEntry[] = [];
  selectedCategories: ChangelogCategory[] = [];
  searchQuery = '';
  isLoading = false;
  error: string | null = null;

  constructor(private changelogService: ChangelogService) {}

  ngOnInit(): void {
    this.loadChangelogData();
  }

  loadChangelogData(): void {
    this.isLoading = true;
    this.error = null;

    this.changelogService
      .getChangelogEntries()
      .pipe(
        catchError((error) => {
          this.error =
            'Failed to load changelog entries. Please try again later.';
          console.error('Error loading changelog:', error);
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((entries) => {
        // Convert date strings to Date objects
        this.allEntries = entries.map((entry) => ({
          ...entry,
          date: new Date(entry.date),
        }));
        this.filteredEntries = [...this.allEntries];
      });
  }

  onCategoryFilterChange(categories: ChangelogCategory[]): void {
    this.selectedCategories = categories;
    this.applyFilters();
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.applyFilters();
  }

  private applyFilters(): void {
    this.filteredEntries = this.allEntries.filter((entry) => {
      // Filter by category if any categories are selected
      const categoryMatch =
        this.selectedCategories.length === 0 ||
        entry.categories.some((category) =>
          this.selectedCategories.includes(category)
        );

      // Filter by search query
      const searchMatch =
        this.searchQuery === '' ||
        entry.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        entry.description
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        entry.details.some((detail) =>
          detail.toLowerCase().includes(this.searchQuery.toLowerCase())
        );

      return categoryMatch && searchMatch;
    });
  }

  getMonthYear(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
}
